'use client'
import { useEffect, useMemo, useRef, useState } from 'react'
import Switch from 'antd/es/switch'
import { ArrowUpOutlined } from '@ant-design/icons'
import ButtonItem from '@/app/[lang]/@desktop/_components/ButtonItem'
import Select from 'Comps/ui-kit/Select'
import HomeFilterPopover from '@/app/[lang]/@desktop/_components/HomeFilterPopover'
import GridLayoutHelper from 'Comps/utils/GridLayoutHelper'
import LoadingImg from '@/icons/LoadingImg'
import useScrollRestoration from 'Hooks/useScrollRestoration'
import {
  homeGenderState,
  homeNSFWState,
  homeSelectedContentTypesState,
  homeSelectedTagIdsState,
  homeSortingState,
  clickedTagsWithBadgeState,
  showFilterBadgeState,
} from 'Store/home'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { SexList, SortingList } from '@/constant/common'
import { TagsNSFW, TagsSFW } from '@/constant/tags'
import { DESKTOP_EXPLORE_PATHNAME } from 'Types/common'
import clsx from 'clsx'
import useI18n from 'Hooks/useI18n'
import useHomeCharacterList from 'Hooks/useHomeCharacterList'
import useCountdown from '@/hooks/useCountdown'
import useGemsProductInfo from '@/hooks/useGemsProductInfo'
import { userState } from 'Store/user'
import { useUrlFilterParams } from 'Hooks/useUrlFilterParams'
import {
  ANNIVERSARY_DEADLINE_TIME,
  HALLOWEEN_END_TIME,
  HALLOWEEN_START_TIME,
  NEW_TAG_END_TIME,
  SUMMER_SALE_DEADLINE_TIME,
  THANKSGIVING_DEADLINE_TIME,
} from '@/constant/date'
import {
  getActivityHomeBannerDisplayScope,
  isHomeActivityBannerVisibleForScope,
} from '@/utils/activity'

const BANNER_INTERVAL = 40
// 稳定的空活动列表引用：activityListData?.list 为空时复用同一引用，避免行内 `|| []`
// 每次渲染产生新数组、击穿 useHomeFeedCells 的 useMemo。
const EMPTY_ACTIVITIES: Activity[] = []
// 网格列类（与 useResponsivePageSize 测量的容器、行虚拟容器内行的 grid 类保持一致）
const HOME_GRID_CLASS_NAME =
  'grid gap-3.5 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 3xl:grid-cols-8'

import { HomeSorting } from '@/types/character'
import { Activity } from '@/types/activity'
import { HomeIconFilter } from '@/icons'
import { getBadgeDotTagNames } from '@/utils/tagDisplay'
import useNavigateSimulatorGame from '@/hooks/simulator-game/useNavigateSimulatorGame'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import useActiveResponsiveSlot from '@/hooks/useActiveResponsiveSlot'
import {
  getDiscoverTabType,
  trackDiscoverSubpageExposure,
  trackDiscoverTabClick,
} from '@/utils/simulatorGameTracking'
import useHomeFirstScreenRum, {
  HOME_INITIAL_PAGE_COUNT,
} from '@/hooks/useHomeFirstScreenRum'
import { getHomeFirstScreenMediaUnits } from '@/utils/homeFirstScreenMedia'
import useResponsivePageSize from '@/hooks/useResponsivePageSize'
import VirtualHomeGrid from '@/components/home/VirtualHomeGrid'
import useHomeFeedCells from '@/app/[lang]/@desktop/_components/useHomeFeedCells'
import { DesktopHomeScrollContainerContext } from '@/components/providers/Providers'
import useHomeActivityList from '@/hooks/useHomeActivityList'

export default function DesktopHomePage() {
  const { t } = useI18n()
  const { trackEvent } = useQuickTracking()
  const isActiveDesktop = useActiveResponsiveSlot('desktop')
  const { navigateToSimulatorGame, worldGameProfileModal } =
    useNavigateSimulatorGame()
  const sorting = useRecoilValue(homeSortingState)
  const isSimulatorGame = sorting === 'World'
  const sex = useRecoilValue(homeGenderState)
  const nsfw = useRecoilValue(homeNSFWState)
  const user = useRecoilValue(userState)
  const { goodsInfo } = useGemsProductInfo()

  const { data: activityListData, shouldReservePendingSlot } =
    useHomeActivityList()

  const { isExpired } = useCountdown(
    user?.user_id ? Number(user?.user_id) / 1000000 + 3 * 86400000 : 0
  )
  const { isExpired: isExpiredCharge } = useCountdown(
    user?.user_id ? Number(user?.user_id) / 1000000 + 5 * 86400000 : 0
  )
  const { isExpired: isExpiredSummerSale } = useCountdown(
    SUMMER_SALE_DEADLINE_TIME
  )
  const { isExpired: isExpiredBlackFri } = useCountdown(
    THANKSGIVING_DEADLINE_TIME
  )
  const { isExpired: isExpiredAnniversary } = useCountdown(
    ANNIVERSARY_DEADLINE_TIME
  )
  const { isExpired: isNewTagExpire } = useCountdown(NEW_TAG_END_TIME)
  const { isExpired: isExpiredHalloween, isStarted: isHalloweenStart } =
    useCountdown(HALLOWEEN_END_TIME, HALLOWEEN_START_TIME)

  // 计算有效的动态 banner 数量
  const validDynamicBannersCount = useMemo(() => {
    const now = new Date().getTime()
    const scope = getActivityHomeBannerDisplayScope(sorting)
    return (
      activityListData?.list?.filter((activity) => {
        return isHomeActivityBannerVisibleForScope(
          activity,
          user?.user_id,
          scope,
          now
        )
      }).length ?? 0
    )
  }, [activityListData, sorting, user?.user_id])

  const showFirstRecharge = useMemo(() => {
    const item = goodsInfo?.find(
      (good) => good?.ProductMeta?.product_id === 'gem_500'
    )

    return !!item && !item?.is_limited_purchase && !!user && !isExpiredCharge
  }, [goodsInfo, isExpiredCharge, user])

  const hasAnyBannerToShow =
    showFirstRecharge ||
    !isExpired ||
    !isExpiredSummerSale ||
    !isExpiredAnniversary ||
    (!isExpiredHalloween && isHalloweenStart) ||
    !isNewTagExpire ||
    !isExpiredBlackFri ||
    validDynamicBannersCount > 0
  const shouldReserveBannerSlot = hasAnyBannerToShow || shouldReservePendingSlot
  // banner 显隐开关：固定引用，避免行内对象字面量每次渲染都是新引用，
  // 击穿下面 useHomeFeedCells 的 useMemo（其依赖数组含 banners）。
  const banners = useMemo(
    () => ({
      showSummerSale: !isExpiredSummerSale,
      showFirstRecharge,
      showNewUser: !isExpired,
      showAnniversaryBanner: !isExpiredAnniversary,
      showHalloweenBanner: !isExpiredHalloween && isHalloweenStart,
      showNewTagsBanner: !isNewTagExpire,
      showBlackFri: !isExpiredBlackFri,
    }),
    [
      isExpiredSummerSale,
      showFirstRecharge,
      isExpired,
      isExpiredAnniversary,
      isExpiredHalloween,
      isHalloweenStart,
      isNewTagExpire,
      isExpiredBlackFri,
    ]
  )
  const [selectedTagIds, setSelectedTagIds] = useRecoilState(
    homeSelectedTagIdsState
  )
  const [selectedContentTypes, setSelectedContentTypes] = useRecoilState(
    homeSelectedContentTypesState
  )
  const [openFilterPopover, setOpenFilterPopover] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const visibleBadgeTags = useMemo(
    () => getBadgeDotTagNames(nsfw ? TagsNSFW : TagsSFW),
    [nsfw]
  )
  const showFilterBadge = useRecoilValue(showFilterBadgeState)
  const realClickedTagsWithBadge = useRecoilValue(clickedTagsWithBadgeState)
  const [filterBadgeMounted, setFilterBadgeMounted] = useState(false)
  const clickedTagsWithBadge = filterBadgeMounted
    ? realClickedTagsWithBadge
    : []
  const setShowFilterBadge = useSetRecoilState(showFilterBadgeState)

  useEffect(() => {
    setFilterBadgeMounted(true)
  }, [])
  const visibleSortingList = useMemo(() => SortingList, [])

  // 从URL参数初始化首页筛选条件
  useUrlFilterParams({
    nsfw,
    sortingList: visibleSortingList,
  })

  // 滚动容器是 page 内层卡片区（main overflow-hidden 钉住筛选栏、外层 layout 不滚），
  // virtua scrollRef / viewportRef / scrollRestoration / back-to-top 都绑它。
  const homeScrollContainerRef = useRef<HTMLDivElement>(null)
  // 用一个常驻的 grid 测量元素来获取真实列数（虚拟滚动下网格按行渲染，没有稳定的整网格容器）
  const gridMeasureRef = useRef<HTMLDivElement>(null)
  // 首屏 pageSize 按真实列数/列宽 + 滚动容器可视高度自适应（去掉写死的 rows）
  const {
    columns,
    pageSize: LIMIT,
    rowHeight,
    rowGap,
    prefetchRows,
  } = useResponsivePageSize(gridMeasureRef, {
    viewportRef: homeScrollContainerRef,
    fallback: 40,
    storageKey: 'home:desktop',
  })
  useScrollRestoration(
    `scroll-restoration:${DESKTOP_EXPLORE_PATHNAME}`,
    homeScrollContainerRef
  )

  const {
    sortingType,
    resetTotalPages,
    handleSelect,
    handleNsfwChange,
    handleSexChange,
    characterListPages,
    characterGetResList,
    homeFirstScreenCacheHit,
    validatingInPages,
    hasMore,
    loadMore,
  } = useHomeCharacterList({
    LIMIT,
    initialPageCount: HOME_INITIAL_PAGE_COUNT,
    device: 'desktop',
  })

  const firstScreenItems = useMemo(
    () => (characterGetResList ?? []).slice(0, LIMIT),
    [characterGetResList, LIMIT]
  )
  const firstScreenMediaUnits = useMemo(
    () =>
      getHomeFirstScreenMediaUnits(firstScreenItems, {
        bannerInterval: BANNER_INTERVAL,
        hasBanner: shouldReserveBannerSlot,
      }),
    [firstScreenItems, shouldReserveBannerSlot]
  )
  const isFirstScreenMediaPending =
    firstScreenMediaUnits.length === 0 && validatingInPages
  const firstScreenRumData = useMemo(
    () => ({
      page: 'home',
      slot: 'desktop',
      sorting,
      sorting_type: sortingType,
      nsfw,
      gender: sex || 'All',
      selected_tag_count: selectedTagIds.length,
      selected_content_type_count: selectedContentTypes.length,
      loaded_item_count: firstScreenItems.length,
      total_loaded_item_count: characterGetResList?.length ?? 0,
      home_data_cache_hit: homeFirstScreenCacheHit,
      // initial_page_count 恒为 1，不占用 QuickTracking 80 属性额度。
      page_size: LIMIT,
    }),
    [
      characterGetResList?.length,
      firstScreenItems.length,
      homeFirstScreenCacheHit,
      LIMIT,
      nsfw,
      selectedContentTypes.length,
      selectedTagIds.length,
      sex,
      sorting,
      sortingType,
    ]
  )
  const { markMediaReady, registerMediaElement } = useHomeFirstScreenRum({
    enabled: isActiveDesktop,
    dataReady: typeof characterListPages !== 'undefined',
    isLoading: isFirstScreenMediaPending,
    mediaUnits: firstScreenMediaUnits,
    scrollContainerRef: homeScrollContainerRef,
    data: firstScreenRumData,
  })
  const getMediaReadyHandler = (key: string) => () => markMediaReady(key)

  useEffect(() => {
    const container = homeScrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      setShowBackToTop(container.scrollTop > 200)
    }

    handleScroll()
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleScrollToTop = () => {
    homeScrollContainerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const tabType = getDiscoverTabType(sorting)
    if (!isActiveDesktop || !tabType) return

    trackDiscoverSubpageExposure(trackEvent, tabType)
  }, [isActiveDesktop, sorting, trackEvent])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !isActiveDesktop) return
      const tabType = getDiscoverTabType(sorting)
      if (!tabType) return
      trackDiscoverSubpageExposure(trackEvent, tabType)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isActiveDesktop, sorting, trackEvent])

  const handleSortingSelect = (value: HomeSorting) => {
    if (value === sorting) return
    const tabType = getDiscoverTabType(value)
    if (isActiveDesktop && tabType) {
      trackDiscoverTabClick(trackEvent, tabType)
    }
    handleSelect(value)
  }

  // 构建扁平 cell 列表（卡片 cell + banner cell）供 VirtualHomeGrid 按列切行。
  // 构建逻辑已抽到 useHomeFeedCells，这里只收集入参。
  const cells = useHomeFeedCells({
    characterGetResList,
    shouldReserveBannerSlot,
    banners,
    dynamicActivities: activityListData?.list ?? EMPTY_ACTIVITIES,
    sorting,
    sex,
    selectedTagIds,
    onNavigateSimulatorGame: navigateToSimulatorGame,
    registerMediaElement,
    getMediaReadyHandler,
    // 首屏卡片数 = 列数 × 一屏行数（prefetchRows 即 ceil(视口高/行步距)），
    // 均为 useResponsivePageSize 的真实测量值，未测量时为 0（不提级）
    priorityCount: columns * prefetchRows,
  })

  return (
    <main className="ml-2.5 flex h-full flex-1 flex-col overflow-hidden pr-4 pt-0">
      <div className="items-center justify-between pb-2 [@media(min-width:800px)]:flex">
        <div
          className="no-scrollbar flex min-w-40 gap-1.5 overflow-x-auto overflow-y-hidden whitespace-nowrap"
          style={{
            paddingRight: '5%',
            maskImage:
              'linear-gradient(to right, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%)',
          }}
        >
          {visibleSortingList.map((text) => (
            <ButtonItem
              key={text}
              value={text}
              selected={sorting === text}
              onClick={(value) => handleSortingSelect(value as HomeSorting)}
              className={clsx('min-h-11 px-[18px] py-[9px] text-sm', {
                'bg-secondary-bg pr-[9px]':
                  sorting === text &&
                  (text === 'Popular' || text === 'Featured'),
                'xl:bg-secondary-bg xl:pr-[9px]':
                  sorting !== text &&
                  (text === 'Popular' || text === 'Featured'),
              })}
            >
              {t(text)}
            </ButtonItem>
          ))}
        </div>
        <div className="relative ml-4 flex max-md:hidden [@media(max-width:800px)]:my-1">
          <div className="flex h-9 items-center gap-4">
            <div className="flex flex-col">
              <div className="text-right text-sm leading-tight text-[#806E74]">
                {t('Limitless')}
              </div>
              <div className="text-[10px] leading-tight text-[#806E74]/70">
                {t('Enable to show Limitless on iOS')}
              </div>
            </div>
            <div className="flex h-6 w-6 translate-y-px cursor-pointer items-center justify-center">
              <Switch
                className={clsx('!min-h-[19px] !min-w-[36px]', {
                  '!bg-[#FF8A00]': nsfw,
                  '!bg-white/20': !nsfw,
                })}
                size="small"
                aria-label={t('Limitless')}
                onChange={handleNsfwChange}
                checked={nsfw}
              />
            </div>
          </div>
          {!isSimulatorGame && (
            <>
              <Select
                className="!border-0 !bg-inherit !text-[#847379]"
                options={SexList.map((item) => ({
                  label: t(item),
                  value: item === 'All' ? 'All' : item,
                }))}
                value={sex || 'All'}
                onChange={handleSexChange}
              />
              {sortingType !== 'Multi-character' &&
                sortingType !== 'Following' && (
                  <>
                    <button
                      type="button"
                      className="relative z-30 ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-secondary-bg"
                      onClick={() => {
                        setOpenFilterPopover((prev) => !prev)
                        if (showFilterBadge) {
                          setShowFilterBadge(false)
                        }
                      }}
                    >
                      <HomeIconFilter />
                      {filterBadgeMounted &&
                        visibleBadgeTags.some(
                          (tag) => !clickedTagsWithBadge.includes(tag)
                        ) && (
                          <span className="absolute right-[8px] top-[7px] h-2 w-2 rounded-full bg-[#FF3B30]" />
                        )}
                    </button>
                    {openFilterPopover && (
                      <>
                        <button
                          type="button"
                          className="fixed inset-0 z-20 cursor-default"
                          aria-label={t('Filter')}
                          onClick={() => setOpenFilterPopover(false)}
                        />
                        <HomeFilterPopover
                          open={openFilterPopover}
                          onClose={() => setOpenFilterPopover(false)}
                          selectedTagIds={selectedTagIds}
                          selectedContentTypes={selectedContentTypes}
                          onSelectTags={(tagIds) => {
                            setSelectedTagIds(tagIds)
                            resetTotalPages()
                          }}
                          onSelectContentTypes={(contentTypes) => {
                            setSelectedContentTypes(contentTypes)
                            resetTotalPages()
                          }}
                        />
                      </>
                    )}
                  </>
                )}
            </>
          )}
        </div>
      </div>
      <div
        ref={homeScrollContainerRef}
        className="desktop-scrollbar-container relative flex-1 overflow-y-auto [overflow-anchor:none] [scrollbar-gutter:stable]"
      >
        <>
          {/* 隐藏的列数测量元素：与卡片网格同样的列类，用于读取真实列数。
              绝对定位脱离滚动流，避免占据 Virtualizer 上方的起点高度（哪怕 h-0，
              它在滚动流里也会让 virtua 的 offset 基准偏一点，导致追加时视口微移）。 */}
          <div
            ref={gridMeasureRef}
            aria-hidden
            className={clsx(
              HOME_GRID_CLASS_NAME,
              'pointer-events-none absolute h-0 w-full overflow-hidden'
            )}
          />
          <DesktopHomeScrollContainerContext.Provider
            value={homeScrollContainerRef}
          >
            <VirtualHomeGrid
              cells={cells}
              columns={columns}
              scrollRef={homeScrollContainerRef}
              gridClassName={HOME_GRID_CLASS_NAME}
              hasMore={hasMore}
              rowHeight={rowHeight}
              rowGap={rowGap}
              onReachEnd={loadMore}
              endThreshold={prefetchRows || undefined}
              footer={
                <>
                  {characterGetResList?.length === 0 &&
                    !validatingInPages &&
                    sortingType === 'Following' && (
                      <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
                        <div className="text-sm text-[#806E74]">
                          {t("Empty. You haven't followed anyone yet.")}
                        </div>
                      </div>
                    )}
                  <GridLayoutHelper
                    listLength={characterListPages?.[0]?.length}
                  />
                  <div className="flex h-20 w-full items-center justify-center">
                    {validatingInPages && <LoadingImg className="scale-[.8]" />}
                  </div>
                </>
              }
            />
          </DesktopHomeScrollContainerContext.Provider>
          {worldGameProfileModal}
        </>
      </div>
      {showBackToTop && (
        <button
          type="button"
          aria-label={/* i18n-ignore */ 'Back to top'}
          className="fixed bottom-[28px] right-4 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-secondary-border bg-secondary-bg backdrop-blur-sm"
          onClick={handleScrollToTop}
        >
          <ArrowUpOutlined className="text-xl leading-none" />
        </button>
      )}
    </main>
  )
}
